'use client'

import { forwardRef } from 'react'
import Link from 'next/link'
import { Logo, LogoIcon } from '@/components/ui/logo'
import { Button } from '@/components/ui/button'
import { Sparkles, ArrowRight } from 'lucide-react'

/**
 * Neon line divider - creates a glowing horizontal line effect
 */
export function NeonLine({ 
  color = 'pink',
  className = '',
  animated = true,
}: { 
  color?: 'pink' | 'cyan' | 'purple' | 'mixed'
  className?: string
  animated?: boolean
}) {
  const colorStyles = {
    pink: {
      line: 'bg-gradient-to-r from-transparent via-pink-500 to-transparent',
      glow: 'bg-pink-500/30',
      shadow: 'shadow-[0_0_15px_rgba(236,72,153,0.5),0_0_30px_rgba(236,72,153,0.3)]',
    },
    cyan: {
      line: 'bg-gradient-to-r from-transparent via-cyan-400 to-transparent',
      glow: 'bg-cyan-400/30',
      shadow: 'shadow-[0_0_15px_rgba(34,211,238,0.5),0_0_30px_rgba(34,211,238,0.3)]',
    },
    purple: {
      line: 'bg-gradient-to-r from-transparent via-violet-500 to-transparent',
      glow: 'bg-violet-500/30',
      shadow: 'shadow-[0_0_15px_rgba(139,92,246,0.5),0_0_30px_rgba(139,92,246,0.3)]',
    },
    mixed: {
      line: 'bg-gradient-to-r from-transparent via-violet-500 via-pink-500 to-transparent',
      glow: 'bg-pink-500/20',
      shadow: 'shadow-[0_0_15px_rgba(236,72,153,0.4),0_0_30px_rgba(139,92,246,0.3)]',
    },
  }

  const style = colorStyles[color]

  return (
    <div className={`relative ${className}`}>
      {/* Glow layer */}
      <div className={`absolute inset-0 h-1 blur-md ${style.glow} ${animated ? 'animate-pulse' : ''}`} />
      {/* Main line */}
      <div className={`relative h-px ${style.line} ${style.shadow}`} />
    </div>
  )
}

/**
 * Neon border box - creates a glowing border effect
 */
export function NeonBorder({ 
  children,
  color = 'pink',
  className = '',
}: { 
  children: React.ReactNode
  color?: 'pink' | 'cyan' | 'purple'
  className?: string
}) {
  const colorStyles = {
    pink: 'border-pink-500/50 shadow-[0_0_15px_rgba(236,72,153,0.3),inset_0_0_15px_rgba(236,72,153,0.1)]',
    cyan: 'border-cyan-400/50 shadow-[0_0_15px_rgba(34,211,238,0.3),inset_0_0_15px_rgba(34,211,238,0.1)]',
    purple: 'border-violet-500/50 shadow-[0_0_15px_rgba(139,92,246,0.3),inset_0_0_15px_rgba(139,92,246,0.1)]',
  }

  return (
    <div className={`border rounded-lg ${colorStyles[color]} ${className}`}>
      {children}
    </div>
  )
}

/**
 * Subtle header bar with iPerform.live branding
 * Appears at the top of public pages without overwhelming the artist content
 */
export const BrandingHeader = forwardRef<HTMLDivElement>(function BrandingHeader(_props, ref) {
  return (
    <div ref={ref} className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-zinc-900/95 via-zinc-900/90 to-zinc-900/95 backdrop-blur-sm border-b border-zinc-800/50 [transform:translate3d(0,0,0)] [-webkit-backface-visibility:hidden]">
      <div className="container max-w-2xl mx-auto px-4 py-2 flex items-center justify-between">
        <Link href="/" className="flex items-center group">
          <Logo size="sm" className="gap-2" />
        </Link>

        <Link
          href="/?from=audience"
          className="text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-1"
        >
          Are you an artist?
          <ArrowRight className="h-2.5 w-2.5" />
        </Link>
      </div>
    </div>
  )
})

/**
 * Enhanced footer with branding and artist recruitment CTA
 */
export function BrandingFooter({ className }: { className?: string }) {
  return (
    <div className={className}>
      {/* Neon Divider with floating icon */}
      <div className="relative py-8">
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2">
          <NeonLine color="mixed" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-zinc-900 px-6">
            <LogoIcon size="sm" />
          </span>
        </div>
      </div>

      {/* Main footer content */}
      <div className="text-center space-y-4 pb-6">
        {/* Logo */}
        <Link href="/" className="inline-block group">
          <Logo size="sm" className="opacity-80 group-hover:opacity-100 transition-opacity" />
        </Link>

        {/* Artist CTA */}
        <div className="max-w-xs mx-auto">
          <p className="text-sm text-zinc-400 mb-3">
            Know a musician who should be on iPerform?
          </p>
          <Link href="/?from=referral">
            <Button 
              variant="outline" 
              size="sm"
              className="border-pink-500/30 bg-pink-500/10 hover:bg-pink-500/20 text-pink-300 hover:text-pink-200 group hover:shadow-[0_0_20px_rgba(236,72,153,0.3)] transition-all"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5 group-hover:animate-pulse" />
              Invite an Artist
              <ArrowRight className="h-3.5 w-3.5 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </Link>
        </div>

        {/* Legal / Links */}
        <div className="flex items-center justify-center gap-4 text-[10px] text-zinc-600">
          <Link href="/terms" className="hover:text-pink-400/70 transition-colors">Terms</Link>
          <span className="text-pink-500/30">•</span>
          <Link href="/privacy" className="hover:text-pink-400/70 transition-colors">Privacy</Link>
          <span className="text-pink-500/30">•</span>
          <span suppressHydrationWarning>© {new Date().getFullYear()} iPerform.live</span>
        </div>
      </div>
    </div>
  )
}

/**
 * Compact "Powered by" badge for inline use
 */
export function PoweredByBadge({ className }: { className?: string }) {
  return (
    <Link 
      href="/" 
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-zinc-800/50 border border-zinc-700/50 hover:border-violet-500/30 hover:bg-violet-500/10 transition-all group ${className}`}
    >
      <LogoIcon size="sm" className="scale-75" />
      <span className="text-[10px] font-medium text-zinc-400 group-hover:text-zinc-300">
        iPerform<span className="neon-text">.live</span>
      </span>
    </Link>
  )
}

